import { FormEvent, ReactNode, useState } from 'react'
import { post } from '@rails/request.js'
import { toast } from 'sonner'
import { api_v1_contacts_path } from '@/lib/routes'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface EmailSignupFormProps {
  defaultPlatform: string
  platform?: string
  platformSelectionUI?: ReactNode
  onSubmit?: () => void
}

export function EmailSignupForm({
  defaultPlatform,
  platform: controlledPlatform,
  platformSelectionUI,
  onSubmit,
}: EmailSignupFormProps) {
  const [first_name, setFirstName] = useState('')
  const [last_name, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [platform, setPlatform] = useState(defaultPlatform)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Use controlled platform if provided, otherwise use internal state
  const platformValue = controlledPlatform ?? platform

  // Map platform from web form format to API format
  const mapPlatformForAPI = (platform: string): string => {
    // If comma-separated (from platform selector), keep as-is for API to handle
    if (platform.includes(',')) {
      return platform
    }
    // Map single platform values
    const lowerPlatform = platform.toLowerCase()
    if (lowerPlatform === 'macos') return 'macos'
    if (lowerPlatform === 'mas') return 'mas'
    if (lowerPlatform === 'windows') return 'windows'
    if (lowerPlatform === 'linux') return 'linux'
    return platform
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await post(api_v1_contacts_path(), {
        body: JSON.stringify({
          email,
          first_name,
          last_name: last_name || undefined,
          properties: {
            platform: mapPlatformForAPI(platformValue),
          },
        }),
        contentType: 'application/json',
        headers: {
          Accept: 'application/json',
        },
      })

      if (!response.ok) {
        const data = await response.json
        // Handle validation errors
        if (response.statusCode === 422 && data.errors) {
          const errorMessage = Array.isArray(data.errors)
            ? data.errors.join(', ')
            : data.errors
          toast.error(errorMessage)
        } else if (response.statusCode === 429) {
          toast.error(
            data.error || 'Too many requests. Please try again later.',
          )
        } else {
          toast.error(
            data.error || 'Something went wrong. Please try again later.',
          )
        }
        setIsSubmitting(false)
        return
      }

      // Success - no need to read response data
      const isMAS = platformValue.toLowerCase() === 'mas'

      if (isMAS) {
        // Open TestFlight page in new tab
        window.open('https://testflight.apple.com/join/zsb55UNR', '_blank')
        toast.success('Thank you! Opening TestFlight...')
      } else {
        toast.success(
          'Thank you! We will let you know when picchat is available to download.',
        )
      }

      setFirstName('')
      setLastName('')
      setEmail('')
      setPlatform(defaultPlatform)
      setIsSubmitting(false)
      onSubmit?.()
    } catch (error) {
      console.error('Error submitting form:', error)
      toast.error('Something went wrong. Please try again later.')
      setIsSubmitting(false)
    }
  }

  return (
    <form
      id="email-signup-form"
      onSubmit={handleSubmit}
      className="flex w-full flex-col gap-4"
    >
      <div className="flex w-full flex-col gap-4 py-4">
        <div className="flex flex-col gap-2 md:flex-row">
          <div className="flex flex-1 flex-col gap-2">
            <Label htmlFor="first_name" className="sr-only">
              First Name
            </Label>
            <Input
              id="first_name"
              name="first_name"
              placeholder="First name *"
              value={first_name}
              onChange={(e) => setFirstName(e.target.value)}
              autoFocus
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <Label htmlFor="last_name" className="sr-only">
              Last Name
            </Label>
            <Input
              id="last_name"
              name="last_name"
              placeholder="Last name"
              value={last_name}
              onChange={(e) => setLastName(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="email" className="sr-only">
            Email
          </Label>
          <Input
            type="email"
            id="email"
            name="email"
            placeholder="email *"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </div>
      </div>
      {platformSelectionUI && (
        <div className="flex w-full flex-col gap-2">{platformSelectionUI}</div>
      )}
    </form>
  )
}
